import { useState } from 'preact/hooks'
import { ChevronDown, ChevronRight } from 'lucide-preact'
import type { LandingPageLayout, LandingPageContextDto, ThankYouPageSettingsDto, PageMetaDto, LandingPageContentImageDto } from '@/types/landingPage'
import type { WebsterAttachment } from '@/types/webster'
import { LandingPageSidebar } from './LandingPageSidebar'
import { FontsSection } from './FontsSection'
import { WebsterChatPanel, type WebsterChatState } from '@/components/webster/WebsterChatPanel'

type AccordionPanel = 'context' | 'typography' | 'chat'

interface LandingPageEditorPanelProps {
    context: LandingPageContextDto | null
    contextLoading: boolean
    selectedLayout: LandingPageLayout
    showAgenda: boolean
    showSpeakerBios: boolean
    showCountdown: boolean
    showDuration: boolean
    includeTracking: boolean
    includeCustomScript: boolean
    onSelectLayout: (layout: LandingPageLayout) => void
    onToggleSetting: (field: 'ShowAgenda' | 'ShowSpeakerBios' | 'ShowCountdown' | 'ShowDuration' | 'IncludeTracking' | 'IncludeCustomScript', value: boolean) => void
    formFields?: string[] | null
    onFormFieldsChange?: (canonicalKeys: string[]) => void
    onContextChange: (field: keyof LandingPageContextDto, value: string | string[]) => void
    thankYouSettings?: ThankYouPageSettingsDto | null
    onThankYouSettingsChange?: (field: keyof ThankYouPageSettingsDto, value: string) => void
    pageMeta?: PageMetaDto | null
    onPageMetaChange?: (next: PageMetaDto) => void
    /** User-supplied content images, shown read-only in the post-gen context panel. */
    contentImages?: LandingPageContentImageDto[]
    /**
     * Chat hook state — passed as a single object so the streaming indicator's
     * live token counter wiring (streamedChars / confirmedTokens) is contained
     * inside <WebsterChatPanel> and doesn't drill through this panel as separate
     * props. Caller passes the relevant subset of useLandingPageChat's return.
     */
    chat: WebsterChatState
    onSend: (message: string, attachments?: WebsterAttachment[]) => void
    /** Upload callback for composer image attachments (paperclip). */
    onUploadImage?: (file: File) => Promise<{ url: string; fileName?: string; contentType?: string }>
    disabled: boolean
    suggestThankYouGeneration?: boolean
    onGenerateThankYou?: () => void
    chatDisabled?: boolean
    /** When another user holds the asset-chat turn, attribute the wait in the
     *  input placeholder ("Joel is asking Webster…") and disable the textarea.
     *  Distinct from `disabled` which represents this user's own in-flight turn. */
    lockedByUserName?: string | null
    webType?: string
    /** Workspace/Web IDs for the fonts panel (upload + apply). */
    workspaceId?: string
    webId?: string
    /** Current effective heading/body font families, and the apply handler. */
    headingFont?: string
    bodyFont?: string
    onApplyFont?: (heading: string | null, body: string | null) => void | Promise<void>
    onRefineFont?: () => void
}

export function LandingPageEditorPanel({
    context,
    contextLoading,
    selectedLayout,
    showAgenda,
    showSpeakerBios,
    showCountdown,
    showDuration,
    includeTracking,
    includeCustomScript,
    onSelectLayout,
    onToggleSetting,
    formFields,
    onFormFieldsChange,
    onContextChange,
    thankYouSettings,
    onThankYouSettingsChange,
    pageMeta,
    onPageMetaChange,
    contentImages,
    chat,
    onSend,
    onUploadImage,
    disabled,
    suggestThankYouGeneration,
    onGenerateThankYou,
    chatDisabled,
    lockedByUserName,
    workspaceId,
    webId,
    headingFont,
    bodyFont,
    onApplyFont,
    onRefineFont,
    webType,
}: LandingPageEditorPanelProps) {
    const [activePanel, setActivePanel] = useState<AccordionPanel>('chat')

    return (
        <div className="w-[420px] xl:w-[480px] shrink-0 border-l border-slate-200 dark:border-slate-700 flex flex-col overflow-hidden bg-white dark:bg-slate-900">
            {/* Context accordion header */}
            <button
                type="button"
                onClick={() => setActivePanel(activePanel === 'context' ? 'chat' : 'context')}
                className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors shrink-0"
            >
                {activePanel === 'context'
                    ? <ChevronDown className="w-4 h-4 text-slate-400" />
                    : <ChevronRight className="w-4 h-4 text-slate-400" />
                }
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    Context & Settings
                </span>
            </button>

            {/* Context panel (expanded) */}
            {activePanel === 'context' && (
                <div className="flex-1 overflow-y-auto">
                    <LandingPageSidebar
                        context={context}
                        contextLoading={contextLoading}
                        selectedLayout={selectedLayout}
                        onSelectLayout={onSelectLayout}
                        showAgenda={showAgenda}
                        showSpeakerBios={showSpeakerBios}
                        showCountdown={showCountdown}
                        showDuration={showDuration}
                        includeTracking={includeTracking}
                        includeCustomScript={includeCustomScript}
                        onToggleSetting={onToggleSetting}
                        formFields={formFields}
                        onFormFieldsChange={onFormFieldsChange}
                        onContextChange={onContextChange}
                        thankYouSettings={thankYouSettings}
                        onThankYouSettingsChange={onThankYouSettingsChange}
                        pageMeta={pageMeta}
                        onPageMetaChange={onPageMetaChange}
                        contentImages={contentImages}
                        workspaceId={workspaceId}
                        webId={webId}
                        headingFont={headingFont}
                        bodyFont={bodyFont}
                        onApplyFont={onApplyFont}
                        onRefineFont={onRefineFont}
                        disabled={disabled}
                        compact
                        readOnly
                        webType={webType}
                    />
                </div>
            )}

            {/* Typography accordion header (editable) — the one place fonts change post-generation.
                The Context & Settings reference above stays read-only. */}
            <button
                type="button"
                onClick={() => setActivePanel(activePanel === 'typography' ? 'chat' : 'typography')}
                className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors shrink-0"
            >
                {activePanel === 'typography'
                    ? <ChevronDown className="w-4 h-4 text-slate-400" />
                    : <ChevronRight className="w-4 h-4 text-slate-400" />
                }
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    Typography
                </span>
            </button>

            {/* Typography panel — kept mounted (visibility toggled, not conditionally rendered) so
                FontsSection loads the font catalog once when the builder opens and retains
                just-uploaded fonts across accordion switches, instead of re-fetching + resetting
                its list on every reopen. */}
            <div className={`flex-1 overflow-y-auto p-5 ${activePanel === 'typography' ? '' : 'hidden'}`}>
                {workspaceId && webId && onApplyFont ? (
                    <FontsSection
                        workspaceId={workspaceId}
                        webId={webId}
                        headingFamily={headingFont}
                        bodyFamily={bodyFont}
                        disabled={disabled}
                        onApply={onApplyFont}
                        onRefine={onRefineFont ? () => { onRefineFont(); setActivePanel('chat') } : undefined}
                    />
                ) : (
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                        Fonts become editable once the page is generated.
                    </p>
                )}
            </div>

            {/* Chat accordion header */}
            <button
                type="button"
                onClick={() => setActivePanel(activePanel === 'chat' ? 'context' : 'chat')}
                className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors shrink-0"
            >
                {activePanel === 'chat'
                    ? <ChevronDown className="w-4 h-4 text-slate-400" />
                    : <ChevronRight className="w-4 h-4 text-slate-400" />
                }
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    Webster Chat
                </span>
            </button>

            {/* Chat panel (expanded) */}
            {activePanel === 'chat' && (
                <WebsterChatPanel
                    chat={chat}
                    onSend={onSend}
                    onUploadImage={onUploadImage}
                    disabled={chat.generating || chatDisabled}
                    lockedByUserName={lockedByUserName}
                    buttonLabel="Send"
                    placeholder="Tell Webster what to change..."
                    suggestThankYouGeneration={suggestThankYouGeneration}
                    onGenerateThankYou={onGenerateThankYou}
                    emptyMessage={
                        <div className="space-y-2">
                            <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                                Chat with Webster
                            </p>
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                                Tell Webster what changes you'd like to make to your landing page.
                            </p>
                        </div>
                    }
                />
            )}
        </div>
    )
}
